import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bot, Loader2, Send as SendIcon, X, Sparkles, ArrowRight } from "lucide-react";
import { api, fmt, formatError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const SUGGESTIONS = [
  "What can I do on iCaard?",
  "What's my wallet balance?",
  "Show my upcoming tickets and orders",
  "How do I book a hostel?",
];

/** Global floating iCaard AI Assistant — available on every page for logged-in users. */
export default function FloatingAIAssistant() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [action, setAction] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const bottomRef = useRef(null);
  const bubbleRef = useRef(null);
  const dragRef = useRef(null);
  const dragged = useRef(false);
  const [position, setPosition] = useState(() => {
    try { const p = JSON.parse(localStorage.getItem("icaard-ai-position")); return Number.isFinite(p?.x) && Number.isFinite(p?.y) ? p : null; }
    catch { return null; }
  });
  const clampPosition = p => ({x:Math.max(8,Math.min(p.x,window.innerWidth-54)),y:Math.max(8,Math.min(p.y,window.innerHeight-54))});
  useEffect(() => {
    const fit = () => setPosition(p => p ? clampPosition(p) : p);
    fit(); window.addEventListener("resize",fit);
    return () => window.removeEventListener("resize",fit);
  }, []);
  function startDrag(e) {
    if (e.button !== 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    dragged.current = false;
    dragRef.current = {x:e.clientX,y:e.clientY,left:rect.left,top:rect.top};
    e.currentTarget.setPointerCapture(e.pointerId);
  }
  function moveDrag(e) {
    const d = dragRef.current;
    if (!d) return;
    if (Math.hypot(e.clientX-d.x,e.clientY-d.y)>6) dragged.current=true;
    if (dragged.current) setPosition(clampPosition({x:d.left+e.clientX-d.x,y:d.top+e.clientY-d.y}));
  }
  function endDrag(e) {
    dragRef.current=null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    if (dragged.current && position) { try { localStorage.setItem("icaard-ai-position",JSON.stringify(position)); } catch {} }
  }

  const firstName = (user?.name || "").split(" ")[0] || "there";

  useEffect(() => {
    if (open && msgs.length === 0) {
      setMsgs([{ role: "assistant", content: `Hello ${firstName} 👋 I'm your iCaard AI assistant. Ask me anything — your money, tickets, orders, bookings, or how to use any iCaard service.` }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, action, open]);

  if (!user) return null;

  const ask = async (text) => {
    const q = (text || input).trim();
    if (!q || busy) return;
    setInput("");
    setAction(null);
    setMsgs((m) => [...m, { role: "user", content: q }]);
    setBusy(true);
    try {
      const { data } = await api.post("/money/assistant", { message: q, session_id: sessionId });
      setSessionId(data.session_id);
      setMsgs((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", content: formatError(e) }]);
    } finally { setBusy(false); }
  };

  const goToSend = () => {};
  void goToSend;

  return (
    <>
      {/* Floating bubble */}
      {!open && (
        <button ref={bubbleRef} onClick={() => { if (!dragged.current) setOpen(true); dragged.current=false; }} data-testid="ai-assistant-bubble"
                onPointerDown={startDrag} onPointerMove={moveDrag} onPointerUp={endDrag} onPointerCancel={endDrag}
                style={{touchAction:"none",cursor:"grab",...(position ? {left:position.x,top:position.y,right:"auto",bottom:"auto",transform:"none"} : {})}}
                title="Drag to move. Tap to open."
                aria-label="Open iCaard AI Assistant"
                className="fixed bottom-[calc(6.5rem+env(safe-area-inset-bottom))] md:bottom-6 right-4 z-30 w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#EC4899] text-white shadow-2xl shadow-[#7C3AED]/40 flex items-center justify-center hover:scale-105 transition-transform">
          <Bot size={24} />
          <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-[#10B981] border-2 border-[#09090F] animate-pulse" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-0 md:bottom-6 right-0 md:right-4 z-[60] w-full md:w-[380px] md:rounded-2xl bg-[#09090F] border-t md:border border-[#26263A] shadow-2xl flex flex-col overflow-hidden"
             style={{ height: "min(560px, 80dvh)" }} data-testid="ai-assistant-panel">
          <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-[#7C3AED]/25 to-[#EC4899]/15 border-b border-[#26263A]">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#EC4899] flex items-center justify-center">
              <Sparkles size={16} className="text-white" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-bold">iCaard AI Assistant</div>
              <div className="text-[10px] text-[#10B981] font-bold">● Online</div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="ai-assistant-close"
                    className="p-2 rounded-lg hover:bg-[#13131E]"><X size={16} /></button>
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-2.5" data-testid="ai-assistant-messages">
            {msgs.map((m, i) => (
              <div key={i} className={`max-w-[85%] rounded-2xl px-3 py-2 text-[13px] whitespace-pre-wrap
                  ${m.role === "user" ? "ml-auto bg-[#7C3AED] text-white" : "bg-[#13131E] border border-[#26263A]"}`}>
                {m.content}
              </div>
            ))}
            {busy && <div className="bg-[#13131E] border border-[#26263A] rounded-2xl px-3 py-2 w-14"><Loader2 size={13} className="animate-spin text-[#A78BFA]" /></div>}
            <div ref={bottomRef} />
          </div>

          {msgs.length <= 1 && (
            <div className="px-3 pb-2 flex flex-wrap gap-1.5">
              {SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => ask(s)}
                        className="text-[10px] px-2.5 py-1 rounded-full border border-[#26263A] text-[#9CA3D4] hover:text-white hover:border-[#7C3AED]">{s}</button>
              ))}
            </div>
          )}

          <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex gap-2 p-3 pb-[max(env(safe-area-inset-bottom),0.75rem)] md:pb-3 border-t border-[#26263A]">
            <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask me anything…"
                   data-testid="ai-assistant-input"
                   className="flex-1 bg-[#13131E] border border-[#26263A] rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#7C3AED]" />
            <button disabled={busy || !input.trim()} data-testid="ai-assistant-send-btn"
                    className="px-3.5 rounded-xl bg-[#7C3AED] hover:bg-[#6D28D9] disabled:opacity-40 text-white">
              <SendIcon size={15} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
